import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import payoutsApi from 'services/api/payouts';
import { useMessageSnackbar, useSubscription } from 'services/hooks';
import { Payout, SubscriptionType } from 'services/api/entities';
import { UpsellModal } from 'components';
import UpgradeDistributionModal from 'components/Modals/UpgradeModal';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { StyledButton } from './styled';
import { WithdrawLimitUndercutDialog } from './components';

interface WithdrawButtonProps {
  payout: Payout;
  onPayoutSuccess: () => void;
}

const WithdrawButton = ({ onPayoutSuccess, payout }: WithdrawButtonProps) => {
  const { t } = useTranslation();
  const [withdrawEarnings, { isLoading }] =
    payoutsApi.useWithdrawEarningsMutation();
  const { hasDistribution, subscriptionType } = useSubscription();
  const { displayErrorSnackbar } = useMessageSnackbar();
  const isFreeSubscription = subscriptionType === SubscriptionType.FREE;
  const [isWithdrawLimitUndercutDialogOpen, setIsWithdrawLimitUndercutOpen] =
    useState(false);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [isUpgradeDistributionModalOpen, setIsUpgradeDistributionModalOpen] =
    useState(false);

  const handleButtonClick = async () => {
    if (isFreeSubscription) {
      setIsUpgradeDistributionModalOpen(true);
      return;
    }
    if (!hasDistribution) {
      setIsUpsellModalOpen(true);
      return;
    }

    if (!payout || !payout.canWithdraw) {
      setIsWithdrawLimitUndercutOpen(true);
      return;
    }

    const response = await withdrawEarnings();
    if ('error' in response) {
      displayErrorSnackbar(t('distribution:unknownPayoutError'));

      return;
    }

    onPayoutSuccess();
  };

  const handleWithdrawLimitUndercutDialogClose = () =>
    setIsWithdrawLimitUndercutOpen(false);
  const handleUpgradeDistributionModalClose = () =>
    setIsUpgradeDistributionModalOpen(false);
  const handleUpsellModalClose = () => setIsUpsellModalOpen(false);

  return (
    <>
      <StyledButton
        variant="outlined"
        onClick={handleButtonClick}
        loading={isLoading}
      >
        {t('distribution:payout:withdraw')}
      </StyledButton>

      <UpgradeDistributionModal
        isOpen={isUpgradeDistributionModalOpen}
        onClose={handleUpgradeDistributionModalClose}
        location={AnalyticsEventLocation.WITHDRAW}
      />

      <UpsellModal
        isOpen={isUpsellModalOpen}
        onClose={handleUpsellModalClose}
        location={AnalyticsEventLocation.WITHDRAW}
      />

      <WithdrawLimitUndercutDialog
        onClose={handleWithdrawLimitUndercutDialogClose}
        isOpen={isWithdrawLimitUndercutDialogOpen}
      />
    </>
  );
};

export default WithdrawButton;
